// Карточка работы (решение владельца 2026-09-07): открывается кликом по названию
// работы на любом экране (кроме шахматки факта — там клик выделяет столбец).
// Три вкладки:
//   «Работа» — поля из справочника с правкой и кнопкой «Досоздать задачи»
//              (удаление работы отсюда убрано 2026-09-10 решением владельца:
//              оно будет отдельным действием в справочнике «Работы»);
//   «Суммарная работа» — по корпусам: задач, начало, окончание и количество бригад
//             (#63, 2026-09-15; этажи раздаются бригадам по очереди);
//   «Ресурсы» — выработка, ресурс и численность (2026-09-08);
//   «Связи» — предшественники и последователи, у каждого видно, сколько связей
//             реально действует в графике (задач, где предшественник найден);
//             там же связь добавляют выбором работы из списка (2026-09-08);
//   «Подрядчики» — кто, где и сколько задач этой работы ведёт;
//   «Работы факта» — переделы работы, по которым отмечается факт (2026-09-11,
//             TZ_RABOTY_FAKT.md): завести, переименовать, порядок, скрыть, удалить;
//             один из них генеральный (★, #12 2026-09-14) — по нему окончание задачи.
import { useCallback, useEffect, useRef, useState } from 'react';
import { call, p, type Data } from './api';
import { confirmBox } from './dialog';
import { ensureBaseTpl } from './links';
import { fmt, kontury } from './util';
import { VyborSpiska } from './Podskazki';

type Work = Data<'/api/projects/{pid}/works', 'get'>[number];
type Card = Data<'/api/works/{wid}/card', 'get'>;
type Spisok = Data<'/api/projects/{pid}/spiski', 'get'>[number];
type Logika = Data<'/api/projects/{pid}/logiki', 'get'>[number];
type WorkFakt = Data<'/api/projects/{pid}/works-fakt', 'get'>[number];
type Summa = Data<'/api/works/{wid}/summa', 'get'>;
export type Vkladka = 'rabota' | 'summa' | 'resursy' | 'svyazi' | 'podr' | 'fakt';

// календарь работы (владелец 2026-09-15, #62)
const KALENDARI: [string, string][] = [['календарные', 'Календарные дни'], ['без праздников', 'Без праздников']];
const RASKATKI: [string, string][] = [
  ['этажи', 'по типовым этажам'], ['подземные', 'по этажам ниже 0'],
  ['технические', 'по техническим этажам'],
  ['квартиры', 'по этажам с квартирами'],
  // #70 (2026-09-18): этаж делится на секции корпуса — задача на каждую секцию
  ['секции', 'по этажам и секциям'],
  ['корпус', 'на корпус'], ['объект', 'на объект'], ['вручную', 'вручную'],
];

export function WorkCard({ pid, workId, workName, can, vstroennaya = false, tolkoRabota = false,
  odnaVkladka, vkladka = 'rabota', onClose, onChanged }: {
  pid: number; workId?: number | null; workName?: string | null; can: boolean;
  /** внутри карточки задачи: без своего окна и заголовка — только содержимое */
  vstroennaya?: boolean;
  /** только поля вкладки «Работа», без своих вкладок и строки сводки — так карточка
    стоит в нижней панели «Детали внизу» (владелец 2026-09-15) */
  tolkoRabota?: boolean;
  /** показать одну вкладку без своих вкладок и сводки — «Суммарная работа» в карточке
    задачи (владелец 2026-09-15); tolkoRabota = odnaVkladka 'rabota' */
  odnaVkladka?: Vkladka;
  /** с какой вкладки открыть (из столбца «Работы факта» справочника — сразу на неё) */
  vkladka?: Vkladka;
  onClose: () => void; onChanged?: (msg?: string) => void;
}) {
  const [work, setWork] = useState<Work | null>(null);
  // очередь сохранений (2026-10-06): два поля подряд (ушёл с «Длительности» и выбрал раскатку)
  // уходили одновременно с одной версией строки, и второе получало «изменил кто-то другой»
  const ochered = useRef<Promise<unknown>>(Promise.resolve());
  const workRef = useRef<Work | null>(null);
  workRef.current = work;
  const [vseRaboty, setVseRaboty] = useState<Work[]>([]);
  const [card, setCard] = useState<Card | null>(null);
  const [spiski, setSpiski] = useState<Spisok[]>([]);
  const [logiki, setLogiki] = useState<Logika[]>([]);
  const [korpusa, setKorpusa] = useState<string[]>([]);
  const [fakty, setFakty] = useState<WorkFakt[]>([]);
  const odna: Vkladka | undefined = odnaVkladka ?? (tolkoRabota ? 'rabota' : undefined);
  const [tab, setTab] = useState<Vkladka>(odna ?? vkladka);
  const [err, setErr] = useState('');

  const load = useCallback(async () => {
    const [w, sp, lg, st] = await Promise.all([
      call<Work[]>('get', p('/api/projects/{pid}/works', { pid })),
      call<Spisok[]>('get', p('/api/projects/{pid}/spiski', { pid })),
      call<Logika[]>('get', p('/api/projects/{pid}/logiki', { pid })),
      call<Record<string, unknown[]>>('get', p('/api/projects/{pid}/structure', { pid })),
    ]);
    if (!w.ok) { setErr(w.error.message); return; }
    const found = w.data.find(x => (workId ? x.id === workId : x.name === workName)) || null;
    workRef.current = found;
    setWork(found);
    setVseRaboty(w.data);
    if (sp.ok) setSpiski(sp.data);
    if (lg.ok) setLogiki(lg.data);
    if (st.ok) setKorpusa(Object.keys(st.data));
    if (found) {
      const [c, wf] = await Promise.all([
        call<Card>('get', p('/api/works/{wid}/card', { wid: found.id })),
        call<WorkFakt[]>('get', p('/api/projects/{pid}/works-fakt', { pid }) + '?work_id=' + found.id),
      ]);
      if (c.ok) setCard(c.data);
      if (wf.ok) setFakty(wf.data);
    }
  }, [pid, workId, workName]);
  useEffect(() => { void load(); }, [load]);

  // «Суммарная работа» (#63, 2026-09-15): грузится при открытии своей вкладки
  const [summa, setSumma] = useState<Summa | null>(null);
  const zagruzitSummu = useCallback(async () => {
    if (!work) return;
    const r = await call<Summa>('get', p('/api/works/{wid}/summa', { wid: work.id }));
    if (r.ok) setSumma(r.data); else setErr(r.error.message);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [work?.id]);
  useEffect(() => { if (tab === 'summa') void zagruzitSummu(); }, [tab, zagruzitSummu]);

  async function sohranitBrigady(korpus: string, znach: string, bylo: number) {
    const n = parseInt(znach, 10);
    if (!work || isNaN(n) || n === bylo) return;
    const r = await call<{ korpus: string; brigad: number }>('put',
      p('/api/works/{wid}/brigady', { wid: work.id }), { korpus, brigad: n });
    if (!r.ok) { setErr(r.error.message); await zagruzitSummu(); return; }
    setErr('');
    await zagruzitSummu();
    onChanged?.('Бригад в корпусе ' + korpus + ': ' + n + '. Нажмите «Пересчитать», чтобы пересчитать даты.');
  }

  function patch(pole: string, znachenie: string | number | boolean | null) {
    const shag = async () => {
      const w = workRef.current;
      if (!w) return;
      // rev — версия строки, которую показала карточка (2026-09-10): если работу
      // успел изменить кто-то другой, сервер ответит 409 и чужую правку не затрёт
      let r = await call<{ id: number }>('patch', p('/api/works/{wid}', { wid: w.id }), { [pole]: znachenie, rev: w.rev });
      if (!r.ok && r.error.code === 'stale') {
        // версия устарела от своей же предыдущей правки — перечитать и повторить один раз
        await load();
        const w2 = workRef.current;
        if (w2) r = await call<{ id: number }>('patch', p('/api/works/{wid}', { wid: w2.id }), { [pole]: znachenie, rev: w2.rev });
      }
      if (!r.ok) { setErr(r.error.message); if (r.error.code === 'stale') await load(); return; }
      setErr('');
      await load();
      onChanged?.();
    };
    ochered.current = ochered.current.then(shag, shag);
    return ochered.current;
  }

  /** новая связь в шаблоне «Базовый»: kem — кто ждёт, kogo — кого ждут */
  async function dobavitSvyaz(kto: string, pred: string) {
    if (!work) return;
    const tid = await ensureBaseTpl(pid);
    if (!tid) { setErr('Не удалось найти схему связей проекта'); return; }
    const r = await call('post', p('/api/link-templates/{tid}/rows', { tid }),
      { work: kto, pred });
    if (!r.ok) { setErr(r.error.message); return; }   // круг или дубль — сообщение с сервера
    setErr('');
    await load();
    onChanged?.('Связь добавлена: «' + pred + '» → «' + kto
      + '». Нажмите «Пересчитать», чтобы обновить даты.');
  }

  /** контуры работы галочками (#43, 2026-09-13): раньше в карточке был список на одно
    значение, и второй контур было не увидеть и не снять; сверка с «Списками» без
    учёта регистра, сохраняется написание из списка */
function KonturyVybor({ znach, vse, can, onSave }: {
  znach: string | null | undefined; vse: string[]; can: boolean; onSave: (v: string[]) => void;
}) {
  const [sel, setSel] = useState<string[]>(() => kontury(znach, vse));
  const [open, setOpen] = useState(false);
  useEffect(() => { setSel(kontury(znach, vse)); }, [znach, vse]);
  const tgl = (o: string) => {
    const next = sel.includes(o) ? sel.filter(x => x !== o) : [...sel, o];
    setSel(next); onSave(next);
  };
  // выпадающий список с галочками вместо россыпи чекбоксов (владелец 2026-09-15):
  // контуров бывает под десяток, и они занимали полкарточки
  return (
    <div className="wc-multi">
      <button type="button" className="sec wc-multi-btn" disabled={!can}
        title="Отметьте контуры работы; это теги для фильтров, на задачи они не влияют"
        onClick={() => setOpen(o => !o)}>
        <span>{sel.length ? sel.join(', ') : 'без контура'}</span><span className="wc-multi-v">▾</span>
      </button>
      {open && (
        <>
          <div className="wc-multi-fon" onClick={() => setOpen(false)} />
          <div className="wc-multi-spisok">
            {vse.map(o => (
              <label key={o}>
                <input type="checkbox" checked={sel.includes(o)} onChange={() => tgl(o)} /> {o}
              </label>
            ))}
            {!vse.length && <div className="muted">В справочнике «Списки» нет контуров.</div>}
            <div className="wc-multi-niz">
              <button className="sec" onClick={() => { setSel([]); onSave([]); setOpen(false); }}>Снять все</button>
              <button className="sec" onClick={() => setOpen(false)}>Готово</button>
            </div>
          </div>
        </>
      )}
    </div>
  );
}

/** корпуса работы: сужение удаляет пустые задачи, расширение досоздаёт */
  async function patchKorpusa(spisokKorpusov: string[]) {
    if (!work) return;
    const r = await call<{ sozdano_zadach?: number; udaleno_zadach?: number }>(
      'patch', p('/api/works/{wid}', { wid: work.id }),
      { korpusa: spisokKorpusov.join(','), rev: work.rev });
    if (!r.ok) { setErr(r.error.message); if (r.error.code === 'stale') await load(); return; }
    setErr('');
    await load();
    onChanged?.('Корпуса сохранены: задач создано ' + (r.data.sozdano_zadach || 0)
      + ', удалено пустых ' + (r.data.udaleno_zadach || 0)
      + '. Нажмите «Пересчитать», чтобы обновить даты.');
  }

  /** досоздать задачи по раскатке: корпус или этажи могли появиться позже работы */
  async function dosozdat() {
    if (!work) return;
    const r = await call<{ sozdano_zadach: number }>('post', p('/api/works/{wid}/expand', { wid: work.id }));
    if (!r.ok) { setErr(r.error.message); return; }
    setErr('');
    await load();
    onChanged?.(r.data.sozdano_zadach
      ? 'Досоздано задач: ' + r.data.sozdano_zadach + '. Нажмите «Пересчитать», чтобы обновить даты.'
      : 'Все задачи по раскатке уже есть — досоздавать нечего.');
  }

  const opts = (kod: string) => spiski.filter(x => x.kod === kod).map(x => x.znachenie);
  // поле = подпись сверху + сам ввод; поля переносятся по ширине окна
  const pole = (label: string, uzel: JSX.Element, hint?: string) => (
    <div className="wc-pole" key={label} title={hint}>
      <span className="wc-lab">{label}</span>
      {uzel}
    </div>
  );
  const vvod = (znach: string | number | null | undefined, na: string, tip = 'text') => (
    <input type={tip} defaultValue={znach ?? ''} disabled={!can}
      onBlur={e => { const v = e.target.value.trim(); if (v !== String(znach ?? '')) void patch(na, v || null); }} />
  );
  // списки — полем с поиском, как поиск работы в панели фильтров (владелец 2026-09-15)
  const spisok = (znach: string | null | undefined, na: string, list: string[]) => (
    <VyborSpiska value={znach ?? ''} disabled={!can} pusto="— не задано —"
      opts={!znach || list.includes(znach) ? list : [znach, ...list]}
      onPick={v => void patch(na, v || null)} />
  );

  return (
    <Obolochka vstroennaya={vstroennaya} onClose={onClose}>
        {!vstroennaya && (
          <div className="wc-top">
            <div className="wc-name">{work ? work.name : (workName || 'Работа')}</div>
            <a className="wc-x" title="Закрыть (Esc)" onClick={onClose}>✕</a>
          </div>
        )}
        {work && !odna && (
          <div className="wc-info">
            Задач в модели: <b>{work.zadach}</b> · связей у работы: <b>{work.svyazej}</b>
            {work.korpusa ? ' · корпуса: ' + work.korpusa : ' · корпуса: все'}
          </div>
        )}
        {!odna && (
          <div className="wc-tabs">
            {([['rabota', 'Работа'], ['summa', 'Суммарная работа'], ['resursy', 'Ресурсы'], ['svyazi', 'Связи'],
               ['podr', 'Подрядчики'],
               ['fakt', 'Работы факта' + (fakty.length ? ' (' + fakty.length + ')' : '')]] as [Vkladka, string][])
              .map(([k, t]) => (
                <button key={k} className={'sec' + (tab === k ? ' on' : '')}
                  onClick={() => setTab(k)}>{t}</button>
              ))}
          </div>
        )}
        <div className="wc-body">
          {err && <div className="err">{err}</div>}
          {!work && <div className="muted">Работа не найдена в справочнике проекта.</div>}

          {work && tab === 'rabota' && (
            <>
              <div className="wc-polya">
              {pole('Название', vvod(work.name, 'name'))}
              {pole('Рабочее название', vvod(work.rab_name, 'rabochee_nazvanie'),
                'Как работу называют на площадке; в шахматке факта показывается оно')}
              {pole('Раскатка', (
                <VyborSpiska value={work.raskatka} disabled={!can} opts={RASKATKI.map(([k]) => k)}
                  podpisi={Object.fromEntries(RASKATKI)} onPick={v => void patch('raskatka', v)} />
              ), 'Как работа превращается в задачи')}
              {pole('Календарь', (
                <VyborSpiska value={work.kalendar} disabled={!can} opts={KALENDARI.map(([k]) => k)}
                  podpisi={Object.fromEntries(KALENDARI)} onPick={v => void patch('kalendar', v)} />
              ), 'Календарные дни — считаются все дни; без праздников — длительность и запас связи не учитывают государственные праздники')}
              {pole('Место', spisok(work.mesto, 'mesto', opts('mesto')))}
              {pole('Контуры', (
                <KonturyVybor znach={work.kontur} vse={opts('kontur')} can={can}
                  onSave={v => void patch('kontur', v.join(',') || null)} />
              ), 'Теги для фильтров и сводок, можно несколько; на создание задач не влияют')}
              {pole('Этап', spisok(work.etap, 'etap', opts('etap')))}
              {pole('Группа работ', spisok(work.sistema, 'uroven2', opts('gruppa')), 'Инженерная система и т.п.; фильтр «Группа работ» в модели (#41)')}
              {pole('Логика монтажа', spisok(work.logika, 'logika_montazha', logiki.map(l => l.name)))}
              {pole('Длительность, дн', vvod(work.dlit, 'dlitelnost', 'number'))}
              {pole('Ед. изм.', spisok(work.ed_izm, 'ed_izm', opts('ed_izm')))}
              {pole('Корпуса', (
                <KorpusaVybor vybrany={work.korpusa || ''} vse={korpusa} can={can}
                  onSave={v => void patchKorpusa(v)} />
              ), 'Где ведётся работа; ничего не отмечено — все корпуса')}
              {pole('Тепловой контур', (
                <label className="wc-tk" title="Работу нельзя выполнять зимой без теплового контура">
                  <input type="checkbox" checked={!!work.tk} disabled={!can}
                    onChange={e => void patch('priznak_tk', e.target.checked)} />да
                </label>
              ))}
              </div>
              {can && (
                <div className="wc-del">
                  <button className="sec"
                    title="Создать недостающие задачи по раскатке — например, если корпус или этажи завели позже работы. Существующие задачи не трогаются"
                    onClick={() => void dosozdat()}>Досоздать задачи</button>
                </div>
              )}
            </>
          )}

          {work && tab === 'summa' && (
            <>
              <table className="tc-preds">
                <thead>
                  <tr>
                    <th>Корпус</th>
                    <th className="tc-p-n">Задач</th>
                    <th className="tc-p-dt">Начало</th>
                    <th className="tc-p-dt">Окончание</th>
                    <th className="tc-p-n">Длит., дн</th>
                    <th className="tc-p-n" title="Сколько бригад ведут эту работу в корпусе: этажи раздаются им по очереди">Бригад</th>
                  </tr>
                </thead>
                <tbody>
                  {!summa && <tr><td colSpan={6} className="muted">Загрузка…</td></tr>}
                  {summa && !summa.stroki.length && <tr><td colSpan={6} className="muted">Задач у работы нет.</td></tr>}
                  {(summa?.stroki || []).map(s => (
                    <tr key={s.korpus}>
                      <td>{s.korpus}</td>
                      <td className="tc-p-n">{s.zadach}</td>
                      <td className="tc-p-dt">{s.nachalo ? fmt(s.nachalo) : '—'}</td>
                      <td className="tc-p-dt">{s.okonchanie ? fmt(s.okonchanie) : '—'}</td>
                      <td className="tc-p-n">{s.dlit ?? '—'}</td>
                      <td className="tc-p-n">
                        <input key={s.korpus + '¶' + s.brigad} type="number" min={1} max={50}
                          defaultValue={s.brigad} disabled={!can} style={{ width: 64 }}
                          title="1 — одна бригада, как раньше; 2 и больше — этажи раздаются бригадам по очереди"
                          onBlur={e => void sohranitBrigady(s.korpus, e.target.value, s.brigad)}
                          onKeyDown={e => { if (e.key === 'Enter') (e.target as HTMLInputElement).blur(); }} />
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
              <div className="pw-muted" style={{ marginTop: 10 }}>
                Бригад больше одной — этажи корпуса раздаются бригадам по очереди: первый этаж
                очереди — бригаде 1, следующий — бригаде 2 и так далее по кругу; бригады идут
                параллельно. Номер бригады, поставленный у задачи вручную, сильнее. Даты
                пересчитываются кнопкой «Пересчитать».
              </div>
            </>
          )}

          {work && tab === 'resursy' && (
            <>
              <div className="wc-polya">
                {pole('Ресурс', spisok(work.resurs, 'resurs_imya', opts('resurs')),
                  'Бригада или звено, которым ведётся работа')}
                {pole('Численность', vvod(work.chel, 'resurs_kolvo', 'number'),
                  'Сколько человек в звене')}
                {pole('Выработка', vvod(work.trud, 'trudoemkost', 'number'),
                  'Объём в день на одного человека')}
              </div>
              <div className="pw-muted" style={{ marginTop: 10 }}>
                Если у задачи задан объём, длительность считается как объём ÷ (выработка ×
                численность), с округлением вверх до дня. Пусто — берётся длительность
                из справочника («Работа»).
              </div>
            </>
          )}

          {work && tab === 'svyazi' && (
            <>
              <div className="wc-podzag">Предшественники — идут перед этой работой</div>
              <SvyaziTabl rows={card?.predshestvenniki || []} />
              {can && (
                <DobavitSvyaz
                  podpis="Добавить предшественника"
                  raboty={vseRaboty.map(x => x.name).filter(n => n !== work.name
                    && !(card?.predshestvenniki || []).some(s => s.rabota === n))}
                  onAdd={n => void dobavitSvyaz(work.name, n)} />
              )}
              <div className="wc-podzag">Последователи — ждут эту работу</div>
              <SvyaziTabl rows={card?.posledovateli || []} />
              {can && (
                <DobavitSvyaz
                  podpis="Добавить последователя"
                  raboty={vseRaboty.map(x => x.name).filter(n => n !== work.name
                    && !(card?.posledovateli || []).some(s => s.rabota === n))}
                  onAdd={n => void dobavitSvyaz(n, work.name)} />
              )}
              <div className="pw-muted" style={{ marginTop: 10 }}>
                Связь заводится по умолчанию как «окончание → начало» без задержки.
                Задержку, сдвиг этажей, мягкость, комментарий и удаление связи ведёт
                экран «Связи».
              </div>
            </>
          )}

          {work && tab === 'podr' && (
            <div className="wc-list">
              {(card?.podryadchiki || []).map((x, i) => (
                <div className="wc-item" key={i}>
                  <div className="wc-item-t">
                    <div className="wc-item-n">{x.podryadchik || '— подрядчик не назначен —'}</div>
                    <div className="wc-item-d">
                      {x.korpusa ? 'корпуса: ' + x.korpusa : 'корпуса не заданы'}
                      {x.nachato ? ' · в работе: ' + x.nachato : ''}
                      {x.zaversheno ? ' · закончено: ' + x.zaversheno : ''}
                    </div>
                  </div>
                  <span className="wc-cnt" title="Задач этой работы у подрядчика">{x.zadach}</span>
                </div>
              ))}
              {!(card?.podryadchiki || []).length && <div className="muted">Задач у работы нет.</div>}
            </div>
          )}

          {work && tab === 'fakt' && (
            <RabotyFakta pid={pid} work={work} spisok={fakty} can={can}
              onErr={setErr}
              onDone={async m => { setErr(''); await load(); onChanged?.(m); }} />
          )}
        </div>
    </Obolochka>
  );
}

/** Работы факта (2026-09-11, TZ_RABOTY_FAKT.md, шаг 1): переделы работы ГПР.
    Факт по ним отмечается в шахматке, а итог ложится в факт задачи. Здесь — только
    справочник: на график он не влияет. Генеральный передел (★, #12, 2026-09-14):
    по нему «окончены» и дата окончания задачи; первый заведённый — генеральный сам,
    его нельзя скрыть, а удалить — только когда он единственный. */
function RabotyFakta({ pid, work, spisok, can, onErr, onDone }: {
  pid: number; work: Work; spisok: WorkFakt[]; can: boolean;
  onErr: (m: string) => void; onDone: (msg?: string) => Promise<void>;
}) {
  const [novoe, setNovoe] = useState('');
  const url = p('/api/projects/{pid}/works-fakt', { pid });
  type Otvet = { id: number; perenesheno?: number; zadach_perescheno?: number };

  /** «итог пересчитан у N задач» — только если что-то пересчиталось */
  const itog = (n?: number) => (n ? '. Итог факта пересчитан у задач: ' + n : '');

  async function dobavit() {
    const name = novoe.trim();
    if (!name) return;
    const aktivnye = spisok.filter(x => x.aktivna);
    if (aktivnye.some(x => x.otmetok) && !await confirmBox(
      'Добавить «' + name + '»? Итог факта задач пересчитается.',
      { ok: 'Добавить',
        podpis: 'У работы стало больше частей: процент готовности незакрытых задач уменьшится. '
          + 'Задачи, закрытые по генеральному переделу, останутся «оконченными».' })) return;
    let r = await call<Otvet>('post', url, { work_id: work.id, name });
    if (!r.ok && r.error.code === 'fakt_est') {
      // первая работа факта у работы, где факт уже введён: переносим его в неё,
      // тогда итог задач не меняется и история цела (ловушка 2 из ТЗ)
      if (!await confirmBox(r.error.message, {
        ok: 'Перенести',
        podpis: 'Отмена — работа факта не заводится, факт задач остаётся как есть.' })) return;
      r = await call<Otvet>('post', url, { work_id: work.id, name, perenesti: true });
    }
    if (!r.ok) { onErr(r.error.message); return; }
    setNovoe('');
    await onDone('Работа факта «' + name + '» добавлена'
      + (r.data.perenesheno ? '. Факт задач перенесён в неё: ' + r.data.perenesheno : '')
      + itog(r.data.zadach_perescheno) + '.');
  }

  async function pravit(wf: WorkFakt, body: Record<string, unknown>, msg?: string) {
    const r = await call<Otvet>('patch', p('/api/works-fakt/{wfid}', { wfid: wf.id }),
      { ...body, rev: wf.rev });
    if (!r.ok) { onErr(r.error.message); if (r.error.code === 'stale') await onDone(); return false; }
    await onDone(msg ? msg + itog(r.data.zadach_perescheno) + '.' : undefined);
    return true;
  }

  /** поменять местами с соседом: порядок столбцов в шахматке */
  async function sdvinut(i: number, kuda: -1 | 1) {
    const a = spisok[i], b = spisok[i + kuda];
    if (!a || !b) return;
    // порядок у двух может совпасть (заведены до нумерации) — тогда разводим явно
    const pa = a.poryadok === b.poryadok ? i + 1 : a.poryadok;
    const pb = a.poryadok === b.poryadok ? i + 1 + kuda : b.poryadok;
    const r1 = await call('patch', p('/api/works-fakt/{wfid}', { wfid: a.id }), { poryadok: pb });
    const r2 = await call('patch', p('/api/works-fakt/{wfid}', { wfid: b.id }), { poryadok: pa });
    if (!r1.ok || !r2.ok) onErr((!r1.ok ? r1.error.message : !r2.ok ? r2.error.message : ''));
    await onDone();
  }

  /** назначить генеральным: у прежнего звезда снимается на сервере той же транзакцией */
  async function naznachit(wf: WorkFakt) {
    if (wf.generalnyj || !wf.aktivna) return;
    if (!await confirmBox('Назначить «' + wf.name + '» генеральным переделом?', {
      ok: 'Назначить',
      podpis: 'Итог факта задач пересчитается: задача будет «окончена» и получит дату окончания, '
        + 'когда закрыт именно этот передел. Остальные переделы влияют только на «начаты» и процент.' })) return;
    await pravit(wf, { generalnyj: true }, 'Генеральный передел — «' + wf.name + '»');
  }

  async function ubrat(wf: WorkFakt) {
    // генеральный не скрывается (сервер ответит 409) — единственный можно только удалить
    if (wf.otmetok && wf.aktivna && !wf.generalnyj) {
      // с отметками по умолчанию не удаляем, а скрываем: отметки целы, вернуть — галочкой
      if (!await confirmBox('Скрыть «' + wf.name + '»?', {
        ok: 'Скрыть',
        podpis: 'По ней отмечено клеток: ' + wf.otmetok + '. Отметки сохранятся, но в итог факта '
          + 'задач и в шахматку она больше не войдёт. Вернуть — галочкой «в работе».' })) return;
      await pravit(wf, { aktivna: false }, 'Работа факта «' + wf.name + '» скрыта');
      return;
    }
    // уже скрытую с отметками можно удалить совсем — с отметками, необратимо
    if (!await confirmBox('Удалить работу факта «' + wf.name + '»?', {
      ok: 'Удалить', opasno: true,
      podpis: wf.otmetok
        ? 'Вместе с ней удалятся отметки: ' + wf.otmetok + '. Это не отменить.'
        : 'Отметок по ней нет — удаляется только строка справочника.' })) return;
    const r = await call<{ zadach_perescheno?: number }>('delete',
      p('/api/works-fakt/{wfid}', { wfid: wf.id }) + (wf.otmetok ? '?force=1' : ''));
    if (!r.ok) { onErr(r.error.message); return; }
    await onDone('Работа факта «' + wf.name + '» удалена' + itog(r.data.zadach_perescheno) + '.');
  }

  return (
    <>
      <div className="pw-muted" style={{ marginBottom: 10 }}>
        На площадке одна работа графика — часто несколько переделов с разными бригадами.
        Здесь они перечисляются, а факт по каждому отмечается в шахматке своим столбцом;
        статус, даты и процент задачи сложатся из них сами. Генеральный передел (★) —
        тот, по которому задача считается оконченной. На график справочник не влияет.
      </div>
      <div className="wc-list">
        {spisok.map((wf, i) => (
          <div className={'wc-item wc-wf' + (wf.aktivna ? '' : ' skryta')} key={wf.id}>
            <button type="button" className={'wc-wf-gen' + (wf.generalnyj ? ' on' : '')}
              disabled={!can || wf.generalnyj || !wf.aktivna}
              title={wf.generalnyj
                ? 'Генеральный передел: когда он окончен — окончена вся задача'
                : !wf.aktivna ? 'Скрытый передел не может быть генеральным — сначала верните галочку «в работе»'
                : can ? 'Назначить генеральным переделом: по нему будет считаться окончание задачи'
                : 'Генеральный передел — тот, по которому окончание задачи'}
              onClick={() => void naznachit(wf)}>{wf.generalnyj ? '★' : '☆'}</button>
            <div className="wc-item-t">
              {can
                ? <input className="wc-wf-name" defaultValue={wf.name} key={wf.name + wf.rev}
                    title="Название работы факта — так будет подписан столбец в шахматке"
                    onBlur={e => {
                      const v = e.target.value.trim();
                      if (v && v !== wf.name) void pravit(wf, { name: v }, 'Переименовано');
                      else e.target.value = wf.name;
                    }}
                    onKeyDown={e => { if (e.key === 'Enter') (e.target as HTMLInputElement).blur(); }} />
                : <div className="wc-item-n">{wf.name}</div>}
              <div className="wc-item-d">
                {wf.generalnyj ? 'генеральный — по нему окончание задачи · ' : ''}
                {wf.aktivna ? '' : 'скрыта — в итог и шахматку не входит · '}
                {wf.otmetok ? 'отмечено клеток: ' + wf.otmetok : 'отметок нет'}
              </div>
            </div>
            {can && (
              <>
                <label className="wc-tk" title={wf.generalnyj
                  ? 'Генеральный передел всегда в работе — чтобы скрыть, сначала назначьте генеральным другой'
                  : 'Снимите, чтобы скрыть: отметки сохранятся, в итог факта задач она не войдёт'}>
                  <input type="checkbox" checked={wf.aktivna} disabled={wf.generalnyj}
                    onChange={e => void pravit(wf, { aktivna: e.target.checked },
                      'Работа факта «' + wf.name + '» ' + (e.target.checked ? 'снова в работе' : 'скрыта'))} />в работе
                </label>
                <span className="wc-wf-kn">
                  <button className="sec" disabled={i === 0} title="Выше — левее в шахматке"
                    onClick={() => void sdvinut(i, -1)}>↑</button>
                  <button className="sec" disabled={i === spisok.length - 1} title="Ниже — правее в шахматке"
                    onClick={() => void sdvinut(i, 1)}>↓</button>
                  <button className="sec" disabled={wf.generalnyj && spisok.length > 1}
                    title={wf.generalnyj && spisok.length > 1
                      ? 'Генеральный передел нельзя удалить, пока есть другие — назначьте генеральным другой'
                      : wf.otmetok && !wf.generalnyj ? 'Скрыть (есть отметки)' : 'Удалить'}
                    onClick={() => void ubrat(wf)}>✕</button>
                </span>
              </>
            )}
          </div>
        ))}
        {!spisok.length && (
          <div className="muted">Работ факта нет — факт отмечается по самой работе, как раньше.</div>
        )}
      </div>
      {can && (
        <div className="bar wc-dob" style={{ marginTop: 10 }}>
          <input value={novoe} placeholder="Новая работа факта, например «Грунтовка»"
            style={{ width: 320, maxWidth: '100%' }}
            onChange={e => setNovoe(e.target.value)}
            onKeyDown={e => { if (e.key === 'Enter') { e.preventDefault(); void dobavit(); } }} />
          <button disabled={!novoe.trim()} onClick={() => void dobavit()}>Добавить</button>
        </div>
      )}
    </>
  );
}

/** окно карточки; во встроенном виде — просто блок внутри карточки задачи */
function Obolochka({ vstroennaya, onClose, children }:
  { vstroennaya: boolean; onClose: () => void; children: React.ReactNode }) {
  if (vstroennaya) return <div className="wc-vstroen">{children}</div>;
  return (
    <div className="dlg-fon" onMouseDown={e => { if (e.target === e.currentTarget) onClose(); }}>
      <div className="dlg wc" role="dialog" aria-modal="true"
        onKeyDown={e => { if (e.key === 'Escape') onClose(); }}>
        {children}
      </div>
    </div>
  );
}

/** Добавление связи из карточки (2026-09-08): выбрал работу — нажал кнопку. */
function DobavitSvyaz({ podpis, raboty, onAdd }:
  { podpis: string; raboty: string[]; onAdd: (rabota: string) => void }) {
  const [v, setV] = useState('');
  return (
    <div className="bar wc-dob">
      <VyborSpiska value={v} opts={raboty} placeholder="выберите работу…" style={{ width: 320 }}
        title="Работа из справочника проекта" onPick={setV} />
      <button disabled={!v} onClick={() => { onAdd(v); setV(''); }}>{podpis}</button>
    </div>
  );
}

/** Корпуса работы: выпадающий список с галочками (2026-09-08).
    Ничего не отмечено — работа ведётся во всех корпусах. */
function KorpusaVybor({ vybrany, vse, can, onSave }:
  { vybrany: string; vse: string[]; can: boolean; onSave: (v: string[]) => void }) {
  const [open, setOpen] = useState(false);
  const sel = vybrany.split(',').map(s => s.trim()).filter(Boolean);
  const tgl = (k: string) => {
    const next = sel.includes(k) ? sel.filter(x => x !== k) : [...sel, k];
    onSave(vse.filter(x => next.includes(x)));   // порядок корпусов проекта
  };
  return (
    <div className="wc-multi">
      <button type="button" className="sec wc-multi-btn" disabled={!can}
        title="Отметьте корпуса; ничего не отмечено — все"
        onClick={() => setOpen(o => !o)}>
        <span>{sel.length ? sel.join(', ') : 'все корпуса'}</span><span className="wc-multi-v">▾</span>
      </button>
      {open && (
        <>
          <div className="wc-multi-fon" onClick={() => setOpen(false)} />
          <div className="wc-multi-spisok">
            {vse.map(k => (
              <label key={k}>
                <input type="checkbox" checked={sel.includes(k)} onChange={() => tgl(k)} /> {k}
              </label>
            ))}
            {!vse.length && <div className="muted">В проекте нет корпусов.</div>}
            <div className="wc-multi-niz">
              <button className="sec" onClick={() => { onSave([]); setOpen(false); }}>Все корпуса</button>
              <button className="sec" onClick={() => setOpen(false)}>Готово</button>
            </div>
          </div>
        </>
      )}
    </div>
  );
}

function SvyaziTabl({ rows }: { rows: Card['predshestvenniki'] }) {
  if (!rows.length) return <div className="muted" style={{ margin: '2px 0 10px' }}>Нет связей.</div>;
  return (
    <div className="wc-list" style={{ marginBottom: 12 }}>
      {rows.map((r, i) => (
        <div className="wc-item" key={i}>
          <div className="wc-item-t">
            <div className="wc-item-n">{r.rabota}</div>
            <div className="wc-item-d">
              {r.tip === 'НН' ? 'начало → начало' : 'окончание → начало'}
              {r.zapas ? ' · задержка ' + r.zapas + ' дн' : ''}
              {r.operezhenie ? ' · этаж ' + (r.operezhenie > 0 ? '+' : '') + r.operezhenie : ''}
              {r.myagkaya ? ' · мягкая: отпускает, если предшественник отстаёт' : ' · жёсткая'}
            </div>
            {r.kom && <div className="wc-item-d" title="Комментарий планера к связи">📝 {r.kom}</div>}
          </div>
          <span className={'wc-cnt' + (r.svyazey ? '' : ' zero')}
            title={r.svyazey ? 'У стольких задач связь действует в графике'
              : 'Связь не действует: предшественника нет на этих этажах'}>
            {r.svyazey}
          </span>
        </div>
      ))}
    </div>
  );
}
